import { Component, OnInit, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@services/security';
import { safeReturnUrl } from '@shared/security/safe-return-url';

@Component({
  standalone: false,
  selector: 'app-login-as-external',
  templateUrl: './login-as-external.component.html',
  styleUrls: ['./login-as-external.component.css']
})
export class LoginAsExternalComponent implements OnInit {
  isChecking: boolean = false;
  errorMessage: string = '';
  hasError: boolean = false;
  successMessage: string = '';
  isSuccess: boolean = false;
  returnUrl: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.returnUrl = safeReturnUrl(this.route.snapshot.queryParams['returnUrl']);
    this.authService.isLoginPage.next(true);
    
    // Récupérer le token depuis les paramètres de l'URL si présent
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      
      if (token) {
        // Token fourni dans l'URL - l'envoyer au backend
        this.authenticateWithExternalToken(token);
      } else {
        // Pas de token dans l'URL - vérifier si présent dans le cookie côté backend
        this.authenticateWithExternalToken(null);
      }
    });
  }

  authenticateWithExternalToken(token: string) {
    this.isChecking = true;
    this.hasError = false;
    this.isSuccess = false;
    this.errorMessage = '';
    this.successMessage = '';

    console.log('[DEBUG] Token envoyé:', token);
    console.log('[DEBUG] Body envoyé:', token ? { Token: token } : null);

    this.authService.loginAsExternal(token).subscribe(
      (response: any) => {   
        console.log('[DEBUG] Réponse reçue:', response);
        
        if (response && !response.IsError) {
          this.signIn(response);
        } else {
          const errorMsg = 'Erreur lors de l\'authentification';
          this.showError(errorMsg);
        }
      },
      (error) => { 
        let errorMsg = 'Une erreur inattendue s\'est produite';
        
        if (error.status === 401) {
          errorMsg = 'Le token est invalide ou a expiré. Veuillez demander un nouveau lien.';
        } else if (error.status === 404) {
          errorMsg = (error.error && error.error.message) || 'Aucun compte espace pro n\'est associé à cet email. Veuillez créer un compte avant de vous connecter.';
        } else if (error.status === 400) {
          errorMsg = (error.error && error.error.message) || 'Requête invalide. Veuillez vérifier le lien utilisé.';
        } else if (error.error && error.error.message) {
          errorMsg = error.error.message;
        }
        
        this.showError(errorMsg);
      }
    );
  }

  signIn(response: any) {
    // TokenModel contient User avec Email, FullName, etc.
    const email = (response.User && response.User.Email) || (response.user && response.user.email) || '';
    const rememberMe = false;
    
    // Récupérer l'email de l'utilisateur externe depuis le token JWT original
    const externalEmail = this.getExternalEmailFromToken();
    
    // Afficher message de succès
    this.isChecking = false;
    this.isSuccess = true;
    this.successMessage = 'Authentification réussie ! Redirection en cours...';
    
    // Délai pour afficher le message avant redirection
    setTimeout(() => {
      this.authService.isLoginPage.next(false);
      this.authService.signIn(response, email, rememberMe, externalEmail);
      
      // Rediriger comme dans login.component.ts
      this.router.navigate(['']);
      if (this.returnUrl != 'undefined' && this.returnUrl != null && this.returnUrl.length > 0 && this.returnUrl != '/') {
        this.zone.runOutsideAngular(() => {
          window.location.href = this.returnUrl;
        });
      }
    }, 1500);
  }

  showError(message: string) {
    this.isChecking = false;
    this.hasError = true;
    this.isSuccess = false;
    this.errorMessage = message;
  }

  retryLogin() {
    this.router.navigate(['/login']);
  }

  getExternalEmailFromToken(): string {
    // Récupérer l'email externe depuis le token passé en paramètre
    const token = this.route.snapshot.queryParams['token'];
    if (!token) return null;
    
    try {
      // Décoder le JWT (partie payload en base64)
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload.external_user_email || payload.email || null;
    } catch (e) {
      console.error('Erreur décodage token:', e);
      return null;
    }
  }
}
